// Phase 2 — Rule-based Fit Summary engine.
//
// CRITICAL: this is a marketing/sales heuristic. It does NOT call the
// production scoring engine, the intelligence report endpoint, or any LLM.
// It is intentionally simple, deterministic, and runs entirely in the browser.
//
// Inputs: answers from discoveryQuestions.js
// Output: a Fit Summary object the Discovery page renders.

import { findUseCase } from "../data/useCases";

const CHALLENGE_TO_FEATURE = {
  find_opps:       { feature: "AI Opportunity Discovery (Phase 3)",      video: "ai-discovery",            learning: ["sam-gov-for-beginners", "market-research-for-federal-opportunities"] },
  choose_bids:     { feature: "Bid/No-Bid Decision Engine",              video: "bid-no-bid",              learning: ["the-bid-no-bid-decision-framework"] },
  competitors:     { feature: "Competitive Intelligence + Incumbent Indicators", video: "competitive-intelligence", learning: ["competitive-intelligence-for-contractors"] },
  teaming:         { feature: "Teaming Partner Discovery",               video: "teaming-partner",         learning: ["teaming-agreements", "subcontracting-strategy"] },
  proposals:       { feature: "Pre-Proposal Intelligence Report (Section L / M risk)", video: "read-a-report", learning: ["writing-a-compliant-proposal", "proposal-compliance-checklists"] },
  pipeline:        { feature: "Prospect Intelligence + CRM Kanban",      video: "prospect-intelligence",   learning: ["capture-fundamentals"] },
  agency_patterns: { feature: "Agency Buying Patterns",                  video: "what-govbidiq-does",      learning: ["agency-buying-patterns"] },
  pricing:         { feature: "Evaluation Criteria Analysis",            video: "opportunity-scoring",     learning: ["pricing-strategy"] },
  compliance:      { feature: "Compliance Risk Surfacing",               video: "read-a-report",           learning: ["proposal-compliance-checklists", "far-101"] },
};

const CATEGORY_TO_USECASE = {
  sdvosb: "sdvosb", vosb: "vosb", "8a": "8a", wosb: "wosb", hubzone: "hubzone",
  sb: "small-business", large: "primes",
};

const VISITOR_TO_USECASE = {
  prime: "primes", sub: "subcontractors", consultant: "consultants",
};

const ENGAGEMENT_GUIDANCE = {
  self:     { fit_strength: "Strong",   note: "Self-service tier is built for your style — sign up and run reports on demand." },
  with_you: { fit_strength: "Strong",   note: "Done-with-you is our highest-leverage motion — recommend a Strategy Call." },
  full:     { fit_strength: "Possible", note: "Full-service requires a scoping call; we can recommend if it fits or refer you out." },
  unsure:   { fit_strength: "Explore",  note: "Start with the Product Tour, then book a Strategy Call once you've seen it." },
};

const HEAVY_VOLUMES = new Set(["25-75", "75+"]);

export function computeFit(answers) {
  const ch = answers.challenges || [];
  const cats = answers.categories || [];

  // Best-fit features (deduped, ordered by selection)
  const features = [];
  const videos = [];
  const learning = [];
  ch.forEach((c) => {
    const m = CHALLENGE_TO_FEATURE[c];
    if (!m) return;
    if (!features.includes(m.feature)) features.push(m.feature);
    if (!videos.includes(m.video)) videos.push(m.video);
    (m.learning || []).forEach((s) => { if (!learning.includes(s)) learning.push(s); });
  });

  // Pick a primary use case
  const setAsideCat = cats.find((c) => CATEGORY_TO_USECASE[c]);
  let useCaseSlug = null;
  if (setAsideCat) useCaseSlug = CATEGORY_TO_USECASE[setAsideCat];
  else if (VISITOR_TO_USECASE[answers.visitor_type]) useCaseSlug = VISITOR_TO_USECASE[answers.visitor_type];
  const useCase = useCaseSlug ? findUseCase(useCaseSlug) : null;

  // Fit strength heuristic
  const engagement = ENGAGEMENT_GUIDANCE[answers.engagement] || ENGAGEMENT_GUIDANCE.unsure;
  const heavyPipeline = HEAVY_VOLUMES.has(answers.bid_volume || "");
  const multiChallenge = ch.length >= 2;
  let fitStrength = engagement.fit_strength;
  if (heavyPipeline && multiChallenge && fitStrength !== "Possible") fitStrength = "Strong";

  // Suggested next step
  let nextStep;
  if (fitStrength === "Strong" && answers.engagement === "self") {
    nextStep = { label: "View Pricing & Start",        to: "/pricing" };
  } else if (fitStrength === "Strong") {
    nextStep = { label: "Request a Strategy Call",     to: "/strategy-call" };
  } else if (fitStrength === "Explore") {
    nextStep = { label: "Take the Product Tour",       to: "/tour" };
  } else {
    nextStep = { label: "View a Sample Report",        to: "/sample" };
  }

  // Visitor-type label resolution
  const VISITOR_LABEL = {
    prime: "Prime contractor", sub: "Subcontractor", consultant: "Capture / proposal consultant",
    agency: "Federal agency / buyer", other: "Contractor",
  };

  return {
    visitor_label: VISITOR_LABEL[answers.visitor_type] || "Contractor",
    set_asides: cats.filter((c) => c !== "none" && c !== "other"),
    challenges: ch,
    features: features.length ? features : ["Opportunity Intelligence Report", "Prospect Scoring"],
    use_case: useCase, // may be null
    recommended_videos: videos.slice(0, 3),
    recommended_learning: learning.slice(0, 4),
    fit_strength: fitStrength,            // "Strong" | "Possible" | "Explore"
    engagement_note: engagement.note,
    next_step: nextStep,
    self_demo_recommended: answers.engagement === "self" || fitStrength === "Strong",
  };
}
